/*
 * Reporte Acumulado de Percepciones por Empleado (ficha 198). El backend suma, por empleado,
 * cada percepción pagada en nóminas Autorizadas del ejercicio (y de los periodos, tipos de
 * nómina empleado y tipos de nómina elegidos; vacío = todos). Aquí solo se arma el Excel: un
 * renglón por empleado, una columna por percepción, TOTAL PERCEPCIONES al final y un renglón
 * de totales; los importes van como número para poder operar con ellos.
 */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Form, Button, Typography, Select as AntSelect, message,
} from 'antd';
import { useSelector } from 'react-redux';
import ExcelJS from 'exceljs';
import moment from 'moment';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import {
  getOpcionesReporteAcumulado,
  getReporteAcumuladoPercepciones,
} from 'api/nominas/reporteAcumuladoPercepciones';
import { ESTATUS_AUTORIZADO } from 'utils/constants';
import {
  FORMATO_MONEDA,
  aplicarEncabezado,
  descargarWorkbook,
  escribirLineasEncabezado,
} from 'utils/excelReportes';

const { Text } = Typography;

const filtrarPorTexto = (input, option) => (option?.label || '')
  .toLowerCase().includes((input || '').toLowerCase());

const ReporteAcumuladoPercepcionesModal = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [ejercicios, setEjercicios] = useState([]);
  const OPCIONES_VACIAS = { periodos: [], tipos_de_nomina_empleado: [], tipos_de_nomina: [] };
  const [opciones, setOpciones] = useState(OPCIONES_VACIAS);
  const tiposSeleccionados = Form.useWatch('tipos_de_nomina_empleado', form);
  const entidad = useSelector((state) => state?.auth?.entidad);

  useEffect(() => {
    let mounted = true;
    if (open && !ejercicios.length) {
      (async () => {
        const res = await getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO });
        const lista = Array.isArray(res) ? res : (res?.results || []);
        if (mounted) setEjercicios(lista);
      })();
    }
    return () => { mounted = false; };
  }, [open]);

  const onChangeEjercicio = async (periodoFiscal) => {
    form.setFieldsValue({ periodos: [], tipos_de_nomina_empleado: [], tipos_de_nomina: [] });
    setOpciones(OPCIONES_VACIAS);
    if (!periodoFiscal) return;
    setLoading(true);
    const res = await getOpcionesReporteAcumulado(periodoFiscal);
    setLoading(false);
    if (res) setOpciones(res);
  };

  // Los periodos dependen del tipo de nómina empleado (PLA-1, EVE-1...): si se eligieron
  // tipos, solo se ofrecen sus periodos.
  const periodosVisibles = useMemo(() => (tiposSeleccionados?.length
    ? opciones.periodos.filter((p) => tiposSeleccionados.includes(p.tipo_de_nomina_empleado_id))
    : opciones.periodos), [opciones.periodos, tiposSeleccionados]);

  const nombres = (ids, catalogo) => {
    const elegidos = catalogo.filter((x) => (ids || []).includes(x.id)).map((x) => x.descripcion);
    return elegidos.length ? elegidos.join(', ') : 'Todos';
  };

  const construirExcel = (data, valores) => {
    const ejercicio = ejercicios.find((e) => e.id === valores.periodo_fiscal);
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('Acumulado');
    escribirLineasEncabezado(ws, [
      'SISTEMA DE NÓMINAS',
      'REPORTE ACUMULADO DE PERCEPCIONES POR EMPLEADO',
      `Entidad: ${entidad?.nombre || ''}`,
      `Ejercicio: ${ejercicio?.periodo || ejercicio?.nombre || valores.periodo_fiscal}`,
      `Periodos: ${nombres(valores.periodos, opciones.periodos)}`,
      `Tipos de Nómina Empleado: ${nombres(valores.tipos_de_nomina_empleado, opciones.tipos_de_nomina_empleado)}`,
      `Tipos de Nómina: ${nombres(valores.tipos_de_nomina, opciones.tipos_de_nomina)}`,
      `Nóminas autorizadas consideradas: ${data.nominas_consideradas} | Generado: ${moment().format('DD/MM/YYYY HH:mm')}`,
    ]);

    const titulos = ['Empleado', 'Nombre del Empleado', ...data.conceptos.map((c) => c.descripcion), 'TOTAL PERCEPCIONES'];
    aplicarEncabezado(ws.addRow(titulos));
    const primeraColumnaImporte = 3;
    const ultimaColumna = titulos.length;

    const formatearImportes = (row) => {
      for (let col = primeraColumnaImporte; col <= ultimaColumna; col += 1) {
        const celda = row.getCell(col);
        celda.numFmt = FORMATO_MONEDA;
      }
    };
    data.filas.forEach((f) => {
      formatearImportes(ws.addRow([f.numero_de_empleado, f.empleado, ...f.importes, f.total]));
    });
    const filaTotal = ws.addRow(['', 'TOTAL', ...data.totales, data.total_general]);
    filaTotal.font = { bold: true };
    formatearImportes(filaTotal);

    ws.getColumn(1).width = 12;
    ws.getColumn(2).width = 45;
    for (let col = primeraColumnaImporte; col <= ultimaColumna; col += 1) {
      ws.getColumn(col).width = 18;
    }
    return workbook;
  };

  const onGenerar = async () => {
    try {
      const valores = await form.validateFields();
      setLoading(true);
      const data = await getReporteAcumuladoPercepciones(valores);
      if (!data) {
        setLoading(false);
        return;
      }
      if (!data.filas?.length) {
        message.info('No hay percepciones pagadas en nóminas autorizadas con los filtros seleccionados.');
      } else {
        const ejercicio = ejercicios.find((e) => e.id === valores.periodo_fiscal);
        await descargarWorkbook(
          construirExcel(data, valores),
          `Reporte_Acumulado_Percepciones_${ejercicio?.periodo || valores.periodo_fiscal}_${moment().format('YYYY-MM-DD')}.xlsx`,
        );
        message.success(`Reporte generado: ${data.filas.length} empleados, ${data.conceptos.length} percepciones`);
      }
      setLoading(false);
    } catch (err) {
      // Errores de validación del formulario: antd ya los muestra en el campo
      setLoading(false);
    }
  };

  const opcionesSelect = (lista) => lista.map((x) => ({ label: x.descripcion, value: x.id }));

  return (
    <Modal
      title="Reporte Acumulado de Percepciones por Empleado"
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={700}
      footer={[
        <Button key="cancelar" onClick={onClose}>Cancelar</Button>,
        <Button key="generar" type="primary" loading={loading} onClick={onGenerar}>
          Generar Excel
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="periodo_fiscal"
          label="Ejercicio"
          rules={[{ required: true, message: 'El campo es requerido' }]}
        >
          <AntSelect
            placeholder="Seleccione el ejercicio"
            options={ejercicios.map((e) => ({
              label: String(e.periodo || e.nombre || e.id), value: e.id,
            }))}
            onChange={onChangeEjercicio}
            showSearch
            filterOption={filtrarPorTexto}
          />
        </Form.Item>
        <Form.Item name="tipos_de_nomina_empleado" label="Tipos de Nómina Empleado" extra="Vacío = todos.">
          <AntSelect
            mode="multiple"
            allowClear
            options={opcionesSelect(opciones.tipos_de_nomina_empleado)}
            filterOption={filtrarPorTexto}
          />
        </Form.Item>
        <Form.Item name="tipos_de_nomina" label="Tipos de Nómina" extra="Vacío = todos.">
          <AntSelect mode="multiple" allowClear options={opcionesSelect(opciones.tipos_de_nomina)} filterOption={filtrarPorTexto} />
        </Form.Item>
        <Form.Item name="periodos" label="Periodos" extra="Vacío = todo el ejercicio.">
          <AntSelect mode="multiple" allowClear options={opcionesSelect(periodosVisibles)} filterOption={filtrarPorTexto} />
        </Form.Item>
        <Text type="secondary">
          Solo se consideran nóminas Autorizadas; las opciones muestran únicamente lo que
          tiene nóminas autorizadas en el ejercicio.
        </Text>
      </Form>
    </Modal>
  );
};

ReporteAcumuladoPercepcionesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ReporteAcumuladoPercepcionesModal;
